import { Button, EButtonColorVariant, SkeletonLine } from '@outdoorsyco/bonfire';
import throttle from 'lodash/throttle';
import React, { MouseEvent, RefObject, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import DatePickerModal from '@/components/common/DatePickerModal/DatePickerModal';
import RatingSummary from '@/components/route/listing/RatingSummary/RatingSummary';
import Heading from '@/components/switchback/Heading/Heading';
import { ESearchFilters } from '@/constants/searchFilters';
import { useAppLayout } from '@/context/AppLayoutContext';
import { useStickyToHeaderBottom } from '@/hooks/useStickyToHeaderBottom';
import { getIsQuoteInSyncWithDates } from '@/redux/selectors/listing/bill';
import {
  getIsListingPublished,
  getListingData,
  getListingLocation,
  TMinimumStayRequirement,
} from '@/redux/selectors/listing/page';

import css from './StickyNav.module.css';

interface IStickyNavProps {
  isCampground?: boolean;
  checkoutURL?: string;
  ctaLabel: string;
  dateFrom?: Date;
  dateFromTime?: number;
  dateTo?: Date;
  dateToTime?: number;
  disabledDays?: {
    from: Date;
    to: Date;
  }[];
  items: {
    label: string;
    anchor: string;
  }[];
  hasInvalidDates?: boolean;
  isUnavailable?: boolean;
  lowerSectionRef: RefObject<HTMLDivElement>;
  minimumDuration: number;
  minimumStayRequirement?: TMinimumStayRequirement;
  upperSectionRef: RefObject<HTMLElement>;
  middleSectionRef?: RefObject<HTMLDivElement>;
  ratingNumber?: number;
  reviewsCount?: number;
  completedTripsCount?: number;
  tripTotalValue?: string;
  tripDuration?: number;
  isCalendarOpen?: boolean;
  onClickRequestToBook: () => void;
  onClickAnchor?: (anchor: string) => void;
  onSelectDate?: (from?: Date, to?: Date) => void;
  onConfirmDates?: (from?: Date, to?: Date, fromTime?: number, toTime?: number) => void;
  onCloseCalendar?: () => void;
}

export const STICKY_NAV_OFFSET = 168;

const StickyNav: React.FC<IStickyNavProps> = ({
  ctaLabel,
  upperSectionRef,
  lowerSectionRef,
  middleSectionRef,
  items,
  dateTo,
  dateFrom,
  dateFromTime,
  dateToTime,
  disabledDays,
  minimumDuration,
  minimumStayRequirement,
  ratingNumber,
  reviewsCount,
  completedTripsCount,
  tripTotalValue,
  tripDuration,
  isCalendarOpen,
  onClickRequestToBook,
  onClickAnchor,
  onConfirmDates,
  onCloseCalendar,
}) => {
  const intl = useIntl();
  const ctaRef = useRef<HTMLDivElement>(null);
  const listing = useSelector(getListingData);
  const listingLocation = useSelector(getListingLocation);
  const isPublished = useSelector(getIsListingPublished);
  // MP-7078: if the loaded quote is for a different date window than the URL,
  // disable the top-nav CTA and skeleton the trip total. Otherwise the guest
  // clicks through on a stale price and checkout rings up something different.
  // A settled quote error resolves `isQuoteInSyncWithDates` to true (the
  // selector treats an error as terminal), so the CTA and shimmer both fall
  // back to the normal "no quote" behavior rather than getting stuck.
  const isQuoteInSyncWithDates = useSelector(getIsQuoteInSyncWithDates);
  const hasSelectedDates = Boolean(dateFrom && dateTo);
  const isTripTotalPending = hasSelectedDates && !isQuoteInSyncWithDates;

  const calendarWarning =
    minimumDuration && minimumDuration > 1
      ? intl.formatMessage(
          {
            defaultMessage:
              'This rental has a {minimumDays} {priceType, select, day {day} other {night}} minimum.',
            id: 'TjE64s',
          },
          { minimumDays: minimumDuration, priceType: 'night' },
        )
      : undefined;

  // Build nearby rentals URL
  const addressParam = listingLocation
    ? encodeURIComponent(`${listingLocation.city},${listingLocation.state}`)
    : '';
  const href = `/rv-search?${ESearchFilters.ADDRESS}=${addressParam}&${ESearchFilters.NEAR_RENTAL}=${listing?.id}`;

  const [isVisible, setIsVisible] = useState(false);
  const [isBookerVisible, setIsBookerVisible] = useState(false);
  const [activeItem, setActiveItem] = useState<string>();
  const [availableItems, setAvailableItems] = useState(items);
  const { headerHeight } = useAppLayout();

  useEffect(() => {
    const onScroll = throttle(() => {
      const upperSectionBottom = upperSectionRef.current?.getBoundingClientRect().bottom;
      const lowerSectionTop = lowerSectionRef.current?.getBoundingClientRect().top;
      const middleSectionTop = middleSectionRef?.current?.getBoundingClientRect().top;

      if (upperSectionBottom && upperSectionBottom <= headerHeight + 10) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }

      if (
        (lowerSectionTop && lowerSectionTop <= STICKY_NAV_OFFSET) ||
        (middleSectionTop && middleSectionTop <= STICKY_NAV_OFFSET)
      ) {
        setIsBookerVisible(true);
      } else {
        setIsBookerVisible(false);
      }

      const itemInView = availableItems.find(item => {
        const el = document.getElementById(item.anchor);
        const rect = el?.getBoundingClientRect();
        if (!rect) {
          return false;
        }
        return rect.top < STICKY_NAV_OFFSET && rect.bottom > STICKY_NAV_OFFSET;
      });

      if (itemInView) {
        setActiveItem(itemInView.anchor);
      }
    }, 50);

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, [availableItems, upperSectionRef, lowerSectionRef, middleSectionRef, headerHeight]);

  useEffect(() => {
    const availableNavItems = items.filter(item => {
      return document.getElementById(item.anchor);
    });

    setAvailableItems(availableNavItems);
  }, [items]);

  const onNavClick = (anchor: string) => (e: MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById(anchor);
    if (element) {
      const currentScroll = window.scrollY;
      window.location.hash = anchor;
      window.scrollTo({ top: currentScroll });

      window.scrollTo({
        behavior: 'smooth',
        top: element.getBoundingClientRect().top + window.pageYOffset - STICKY_NAV_OFFSET,
      });
    }

    if (onClickAnchor) onClickAnchor(anchor);
  };

  const { stickyTopPositionRef } = useStickyToHeaderBottom();

  return (
    <div
      ref={stickyTopPositionRef}
      className="fixed z-50 hidden w-full overflow-hidden pointer-events-none md:block">
      <div
        data-visible={isVisible}
        data-testid="sticky-header"
        id="listing-sticky-header"
        className={`py-7 lg:py-0 lg:h-[68px] bg-white transform -translate-y-full transition-transform duration-200 ease-in-out border-b border pointer-events-auto ${css.bar}`}>
        <div className="grid grid-cols-main container-main gap-x-default lg:h-full">
          <div className="flex items-center col-span-9 col-start-1 lg:h-full">
            {availableItems.map(({ label, anchor }) => (
              <span key={label} className="inline-block pr-7 last:pr-0 lg:pr-3 xl:pr-7">
                <a
                  href={`#${anchor}`}
                  onClick={onNavClick(anchor)}
                  data-testid={`${anchor}-btn`}
                  data-current={activeItem === anchor}
                  className={`text-gray-500 highlight autoType400 lg:text lg:autoType300 xl:highlight xl:autoType400 ${css.link}`}>
                  {label}
                </a>
              </span>
            ))}
          </div>
          {!isPublished ? (
            <div className="justify-end hidden lg:flex lg:col-start-10 xl:col-start-11 lg:col-span-7 xl:col-span-6">
              <div
                data-testid="unpublished-nav"
                className="flex items-center py-4 whitespace-nowrap">
                <Heading level={2} className="mb-3 highlight autoType800">
                  <FormattedMessage
                    defaultMessage="Oops, this listing isn't active right now."
                    id="vS8dFg"
                    description="Listing Page > Bill Module"
                  />
                </Heading>
                <Button
                  className="ml-4 whitespace-nowrap"
                  href={href}
                  variant={EButtonColorVariant.Primary}
                  label={intl.formatMessage({
                    defaultMessage: 'Explore other options',
                    id: 'f0m/Sl',
                  })}
                />
              </div>
            </div>
          ) : (
            <div className="items-center justify-end hidden lg:flex lg:col-start-10 xl:col-start-11 lg:col-span-7 xl:col-span-6 gap-4">
              {isBookerVisible && !(dateFrom && dateTo) ? (
                <div className="flex flex-col items-end justify-center">
                  <span className="text-sm font-semibold">
                    <FormattedMessage
                      id="8C/L0m"
                      defaultMessage="Add dates for prices"
                      description="Listing > Sticky nav > placeholder when no dates selected"
                    />
                  </span>
                  <RatingSummary
                    ratingNumber={ratingNumber}
                    reviewsCount={reviewsCount}
                    completedTripsCount={completedTripsCount}
                  />
                </div>
              ) : isBookerVisible ? (
                <div className="flex flex-col items-end justify-center">
                  {tripTotalValue ? (
                    <span className="text-sm font-semibold">
                      {tripTotalValue}
                      {tripDuration ? (
                        <span className="font-normal text-gray-600">
                          {' '}
                          {listing?.active_options?.use_day_pricing
                            ? intl.formatMessage(
                                {
                                  id: '/DiqrB',
                                  defaultMessage: 'for {days, plural, one {# day} other {# days}}',
                                  description:
                                    '[Price]: total price duration for a day-priced listing',
                                },
                                { days: tripDuration },
                              )
                            : intl.formatMessage(
                                {
                                  id: 'jzRwv5',
                                  defaultMessage:
                                    'for {nights, plural, one {# night} other {# nights}}',
                                  description: '[Price]: total price formatting for a listing',
                                },
                                { nights: tripDuration },
                              )}
                        </span>
                      ) : null}
                    </span>
                  ) : isTripTotalPending ? (
                    <SkeletonLine
                      width="140px"
                      className="bg-gray-200"
                      data-testid="sticky-nav-total-pending"
                    />
                  ) : null}
                  <RatingSummary
                    ratingNumber={ratingNumber}
                    reviewsCount={reviewsCount}
                    completedTripsCount={completedTripsCount}
                  />
                </div>
              ) : null}
              {isBookerVisible && (
                <div ref={ctaRef} className="relative">
                  <Button
                    onClick={onClickRequestToBook}
                    variant={EButtonColorVariant.Primary}
                    label={ctaLabel}
                    disabled={isTripTotalPending}
                    data-testid="sticky-nav-cta-btn"
                  />
                  <DatePickerModal
                    disabledDays={disabledDays}
                    initialFrom={dateFrom}
                    initialFromTime={dateFromTime}
                    initialTo={dateTo}
                    initialToTime={dateToTime}
                    minimumStayRequirement={minimumStayRequirement}
                    onConfirmDates={(from, to, fromTime, toTime) => {
                      onConfirmDates?.(from, to, fromTime, toTime);
                      onCloseCalendar?.();
                    }}
                    onDismiss={() => onCloseCalendar?.()}
                    relativeTo={ctaRef.current}
                    show={isCalendarOpen ?? false}
                    warning={calendarWarning}
                    allowSameDay={false}
                    requestTime={false}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StickyNav;
